/* eslint-disable no-loop-func */
import { call, put, all, takeEvery, takeLatest } from 'redux-saga/effects';
import { apolloQuery, apolloMutation } from 'utils/apollo';
import {
	QUERY_DEVICE_LIST,
	QUERY_DEVICE,
	CREATE_DEVICE,
	UPDATE_DEVICE,
	WRITE_SHADOW,
	DELETE_DEVICE,
	UPDATE_SCHEMA,
	UPDATE_TRIGGER,
	QUERY_TRIGGER,
} from 'utils/apollo/graphql/device';
import { QUERY_PROJECT } from 'utils/apollo/graphql/project';
import * as actions from 'redux/actions/deviceAction';
import schema from 'constants/schema.json';
import trigger from 'constants/trigger.json';
import { push } from 'connected-react-router';

function* loadDevices(action) {
	try {
		const { filter } = action.payload;

		const project = yield call(async () => await apolloQuery(QUERY_PROJECT, { projectid: filter.projectid }));
		if (project.errors) throw project.errors;

		const total = project.data.project.numberdevice;

		let devices = [];
		let i = 0;
		while (i < total) {
			const offset = i + 100;
			const { data, errors } = yield call(
				async () =>
					await apolloQuery(QUERY_DEVICE_LIST, {
						filter: { projectid: filter.projectid, tags: { A: 'handysense' } },
						limit: `${i},${offset}`,
					})
			);

			if (errors) {
				throw errors;
			} else {
				const newDevices = devices.concat(data.deviceList);
				devices = newDevices;
			}
			i = offset;
		}
		yield put(actions.loadDevices.success(devices));
	} catch (error) {
		yield put(actions.loadDevices.failure(error));
	}
}

function* loadDevice(action) {
	try {
		const { deviceId } = action.payload;
		const { data, errors } = yield call(async () => await apolloQuery(QUERY_DEVICE, { deviceid: deviceId }));
		const TriggerRes = yield call(async () => await apolloQuery(QUERY_TRIGGER, { deviceid: deviceId }));
		if (TriggerRes.errors) throw TriggerRes.errors;

		if (errors) {
			throw errors;
		} else {
			yield put(actions.loadDevice.success({ ...data.device, trigger: TriggerRes.data.trigger.value }));
		}
	} catch (error) {
		yield put(actions.loadDevice.failure(error));
	}
}

function* createDevice(action) {
	try {
		const { device } = action.payload;
		const mutationRes = yield call(
			async () =>
				await apolloMutation(CREATE_DEVICE, {
					projectid: device.projectid,
					deviceinfo: {
						alias: device.alias,
						description: device.description,
						tag: device.tag,
						tags: { A: 'handysense' },
					},
				})
		);
		if (mutationRes.errors) throw mutationRes.errors;

		// write device name to shadow
		const writeShadowRes = yield call(
			async () =>
				await apolloMutation(WRITE_SHADOW, {
					deviceid: mutationRes.data.createDevice.deviceid,
					data: { name: device.alias },
				})
		);
		if (writeShadowRes.errors) throw writeShadowRes.errors;

		// write schema and trigger
		const writeSchemaRes = yield call(
			async () =>
				await apolloMutation(UPDATE_SCHEMA, {
					deviceid: mutationRes.data.createDevice.deviceid,
					value: JSON.stringify(schema),
				})
		);
		if (writeSchemaRes.errors) throw writeSchemaRes.errors;

		const updateTriggerRes = yield call(
			async () =>
				await apolloMutation(UPDATE_TRIGGER, {
					deviceid: mutationRes.data.createDevice.deviceid,
					value: JSON.stringify(trigger),
				})
		);
		if (updateTriggerRes.errors) throw updateTriggerRes.errors;

		//-----device list
		const project = yield call(async () => await apolloQuery(QUERY_PROJECT, { projectid: device.projectid }));
		if (project.errors) throw project.errors;

		const total = project.data.project.numberdevice;

		let devices = [];
		let i = 0;
		while (i < total) {
			const offset = i + 100;
			const { data, errors } = yield call(
				async () =>
					await apolloQuery(QUERY_DEVICE_LIST, {
						filter: { projectid: device.projectid, tags: { A: 'handysense' } },
						limit: `${i},${offset}`,
					})
			);

			if (errors) {
				throw errors;
			} else {
				const newDevices = devices.concat(data.deviceList);
				devices = newDevices;
			}
			i = offset;
		}

		yield put(actions.createDevice.success(devices, mutationRes.data.createDevice));
		yield put({ type: 'CLEAR_MSG' });
	} catch (error) {
		yield put(actions.createDevice.failure(error));
	}
}

function* updateDevice(action) {
	try {
		const { projectId, deviceId, device } = action.payload;

		const mutationRes = yield call(
			async () =>
				await apolloMutation(UPDATE_DEVICE, {
					deviceid: deviceId,
					deviceinfo: {
						alias: device.alias,
						description: device.description,
						tag: device.tag,
						tags: device.tags,
					},
				})
		);
		if (mutationRes.errors) throw mutationRes.errors;

		// update name to shadow
		const writeShadowRes = yield call(
			async () => await apolloMutation(WRITE_SHADOW, { deviceid: deviceId, data: { name: device.alias } })
		);
		if (writeShadowRes.errors) throw writeShadowRes.errors;

		//-----device list
		const project = yield call(async () => await apolloQuery(QUERY_PROJECT, { projectid: projectId }));
		if (project.errors) throw project.errors;

		const total = project.data.project.numberdevice;

		let devices = [];
		let i = 0;
		while (i < total) {
			const offset = i + 100;
			const { data, errors } = yield call(
				async () =>
					await apolloQuery(QUERY_DEVICE_LIST, { filter: { projectid: projectId }, limit: `${i},${offset}` })
			);

			if (errors) {
				throw errors;
			} else {
				const newDevices = devices.concat(data.deviceList);
				devices = newDevices;
			}
			i = offset;
		}
		const oldGroupId = device.oldGroupId ? device.oldGroupId : null;
		const devicesIngroup = devices.filter((device) => device.groupid === oldGroupId);

		yield put(actions.updateDevice.success(devices, mutationRes.data.updateDevice, devicesIngroup));
	} catch (error) {
		yield put(actions.updateDevice.failure(error));
	}
}

function* deleteDevice(action) {
	try {
		const { projectId, deviceId, groupId } = action.payload;
		const mutationRes = yield call(
			async () => await apolloMutation(DELETE_DEVICE, { deviceid: deviceId, groupId })
		);

		//-----device list
		const project = yield call(async () => await apolloQuery(QUERY_PROJECT, { projectid: projectId }));
		if (project.errors) throw project.errors;

		const total = project.data.project.numberdevice;

		let devices = [];
		let i = 0;
		while (i < total) {
			const offset = i + 100;
			const { data, errors } = yield call(
				async () =>
					await apolloQuery(QUERY_DEVICE_LIST, { filter: { projectid: projectId }, limit: `${i},${offset}` })
			);

			if (errors) {
				throw errors;
			} else {
				const newDevices = devices.concat(data.deviceList);
				devices = newDevices;
			}
			i = offset;
		}
		const devicesIngroup = devices.filter((device) => device.groupid === groupId);

		if (mutationRes.errors) {
			throw mutationRes.errors;
		} else {
			yield put(actions.deleteDevice.success(devices, mutationRes.data.deleteDevice, devicesIngroup));
			yield put(push(`/p/${projectId}`));
			yield put({ type: 'CLEAR_MSG' });
		}
	} catch (error) {
		yield put(actions.deleteDevice.failure(error));
	}
}

function* updateShadow(action) {
	try {
		const { deviceId, cmd, timestamp } = action.payload;

		const { data, errors } = yield call(async () => {
			if (!timestamp) {
				await apolloMutation(WRITE_SHADOW, {
					deviceid: deviceId,
					data: { ...cmd },
				});
			} else {
				await apolloMutation(WRITE_SHADOW, {
					deviceid: deviceId,
					data: { ...cmd },
					timestamp: timestamp,
				});
			}
		});
		if (errors) {
			throw errors;
		} else {
			yield put(actions.updateShadow.success(data.writeShadow));
		}
	} catch (error) {
		yield put(actions.updateShadow.failure(error));
	}
}

function* setNotifyStatus(action) {
	try {
		const { deviceId, json } = action.payload;
		const { errors } = yield call(
			async () => await apolloMutation(UPDATE_TRIGGER, { deviceid: deviceId, value: json })
		);

		if (errors) {
			throw errors;
		} else {
			yield put(actions.setNotifyStatus.success());
		}
	} catch (error) {
		yield put(actions.setNotifyStatus.failure(error));
	}
}

function* updateDevicesStatus(action) {
	try {
		const { devices, deviceid, status } = action.payload;

		const NEW_DEVICES = devices?.map((device) => (device.deviceid === deviceid ? { ...device, status } : device));
		yield put(actions.updateDevicesStatus.success(NEW_DEVICES));
	} catch (error) {
		yield put(actions.updateDevicesStatus.failure());
	}
}

export default function* watchDevices() {
	yield all([
		takeLatest(actions.LOAD_DEVICES.REQUEST, loadDevices),
		takeLatest(actions.LOAD_DEVICE.REQUEST, loadDevice),
		takeEvery(actions.CREATE_DEVICE.REQUEST, createDevice),
		takeEvery(actions.UPDATE_DEVICE.REQUEST, updateDevice),
		takeEvery(actions.DELETE_DEVICE.REQUEST, deleteDevice),
		takeEvery(actions.UPDATE_SHADOW.REQUEST, updateShadow),
		takeEvery(actions.SET_NOTIFY_STATUS.REQUEST, setNotifyStatus),
		takeLatest(actions.UPDATE_DEVICES_STATUS.REQUEST, updateDevicesStatus),
	]);
}
